import React, { useMemo } from 'react';

import { css, cx } from '@emotion/css';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles.ts';
import { ConditionType, MedicationSchema } from '@/models/ConditionModel.ts';
import {
  ChipChoice,
  ChipSelector,
} from '@/uiKit/molecules/ControlledChipChoice.tsx';

export type InsulinSchemeSelectionFieldProps = {
  className?: string;
  allowedInsulinSchemes: MedicationSchema[];
};

export const InsulinSchemeSelectionField: React.FC<
  InsulinSchemeSelectionFieldProps
> = ({ className, allowedInsulinSchemes }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const diabetesType = useWatch({
    name: 'diabetesType',
  }) as ConditionType | undefined;
  const choices: ChipChoice[] = useMemo(() => {
    let showPumpComingSoonMessage = true;
    const choices: ChipChoice[] =
      allowedInsulinSchemes
        .filter(type => {
          if (diabetesType !== 'gestational' && type === 'noInsulin') {
            return false;
          }

          if (diabetesType === 'gestational' && type === 'insulinPump') {
            showPumpComingSoonMessage = false; // no pumps for gestational
            return false;
          }

          if (type === 'insulinPump') {
            showPumpComingSoonMessage = false; // pumps are already available
          }
          return true;
        })
        .map(type => ({
          displayName: t(`diabetesForms.insulin.${type}`),
          value: type,
        })) || [];
    if (showPumpComingSoonMessage && diabetesType !== 'gestational') {
      choices.push({
        displayName: t('diabetesForms.insulin.insulinPump'),
        value: 'insulinPump',
        disabled: true,
        tooltip: t('diabetesForms.insulin.insulinPumpsComingSoon'),
      });
    }

    return choices;
  }, [allowedInsulinSchemes, diabetesType, t]);

  return (
    <div className={cx(styles.container, className)}>
      <ChipSelector
        id="tls-insulin-type-chips"
        label={t('diabetesForms.insulinScheme')}
        fieldName="insulinScheme"
        choices={choices}
      />
    </div>
  );
};

const makeStyles = () => ({
  container: css({}),
});
